// The book composer's fields (Figma eWr6sqSGfDufrRNUMjbtK4 `Web / Books / Composer modal` 2767:1081) and their
// request body. Pure: the modal keeps a `BookDraft` and turns it into the create / update body here.
import type { BookInput, PostV2 } from "../../api/content";
import { CURRENCY, priceCentsOf } from "../events/eventDraft";

export interface BookDraft {
  title: string;
  description: string;
  /** The uploaded (READY, PRIVATE) PDF. */
  pdf: { fileId: string; name: string; pages: number } | null;
  /** The author's cover: uploaded now, or the stored one of an edit (sent back so the update keeps it). null = the PDF's first page. */
  cover: { fileId: string; url: string } | null;
  authorName: string;
  language: string;
  pages: string;
  originalAuthor: string;
  originalYear: string;
  originalLanguage: string;
  translator: string;
  translationYear: string;
  translationLanguage: string;
  recommendedAge: string;
  /** Euros as typed ("12,50"); empty = free. */
  price: string;
  supportUrl: string;
  /** Carried over from the stored book on edit (not editable here). */
  currency?: string;
  shopUrl?: string;
}

export type BookField = "title" | "pdf" | "price" | "pages" | "originalYear" | "translationYear" | "supportUrl";

export const MAX_PDF_BYTES = 100 * 1024 * 1024;

export function newBookDraft(): BookDraft {
  return {
    title: "", description: "", pdf: null, cover: null, authorName: "", language: "", pages: "",
    originalAuthor: "", originalYear: "", originalLanguage: "", translator: "", translationYear: "", translationLanguage: "",
    recommendedAge: "", price: "", supportUrl: "",
  };
}

/** An edit's fields from the stored post (its PDF item and `book` facts). */
export function bookDraftOf(p: PostV2): BookDraft {
  const b = p.book;
  const item = p.items.find((it) => it.fileId != null);
  const price = b?.priceCents == null ? "" : (b.priceCents / 100).toFixed(2);
  return {
    title: p.title ?? "",
    description: p.description ?? p.caption ?? "",
    pdf: item?.fileId ? { fileId: item.fileId, name: item.title ?? "", pages: item.pdf?.pageCount ?? b?.pages ?? 0 } : null,
    // Only the author's own cover (the first page of the PDF is no cover to send back).
    cover: b?.coverFileId ? { fileId: b.coverFileId, url: b.coverUrl ?? "" } : null,
    authorName: b?.authorName ?? "",
    language: b?.language ?? "",
    pages: b?.pages != null ? String(b.pages) : "",
    originalAuthor: b?.original?.author ?? "",
    originalYear: b?.original?.year != null ? String(b.original.year) : "",
    originalLanguage: b?.original?.language ?? "",
    translator: b?.translation?.translator ?? "",
    translationYear: b?.translation?.year != null ? String(b.translation.year) : "",
    translationLanguage: b?.translation?.language ?? "",
    recommendedAge: b?.recommendedAge ?? "",
    price,
    supportUrl: b?.supportUrl ?? "",
    currency: b?.currency,
    shopUrl: b?.shopUrl,
  };
}

const isYear = (s: string) => /^\d{1,4}$/.test(s) && Number(s) > 0;

function isWebUrl(s: string): boolean {
  try {
    const u = new URL(s);
    return (u.protocol === "http:" || u.protocol === "https:") && u.host !== "";
  } catch {
    return false;
  }
}

/** Fields that cannot be sent as they are; the composer shows each one's reason under it after a tap on Post. */
export function bookErrors(d: BookDraft): Partial<Record<BookField, true>> {
  const out: Partial<Record<BookField, true>> = {};
  if (d.title.trim() === "") out.title = true;
  if (d.pdf == null) out.pdf = true;
  if (priceCentsOf(d.price) === undefined) out.price = true;
  const pages = d.pages.trim();
  if (pages !== "" && !(/^\d{1,6}$/.test(pages) && Number(pages) > 0)) out.pages = true;
  if (d.originalYear.trim() !== "" && !isYear(d.originalYear.trim())) out.originalYear = true;
  if (d.translationYear.trim() !== "" && !isYear(d.translationYear.trim())) out.translationYear = true;
  if (d.supportUrl.trim() !== "" && !isWebUrl(d.supportUrl.trim())) out.supportUrl = true;
  return out;
}

const orUndef = (s: string): string | undefined => (s.trim() === "" ? undefined : s.trim());

/** The create / update body's parts (call only when `bookErrors` is empty). */
export function toBookInput(d: BookDraft): { title: string; description: string | undefined; items: { fileId: string }[]; book: BookInput } {
  const original = {
    author: orUndef(d.originalAuthor), year: orUndef(d.originalYear) ? Number(d.originalYear.trim()) : undefined, language: orUndef(d.originalLanguage),
  };
  const translation = {
    translator: orUndef(d.translator), year: orUndef(d.translationYear) ? Number(d.translationYear.trim()) : undefined, language: orUndef(d.translationLanguage),
  };
  const has = (o: Record<string, unknown>) => Object.values(o).some((v) => v !== undefined);
  const clean = <T extends Record<string, unknown>>(o: T) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as T;
  const book: BookInput = {
    ...(d.cover?.fileId ? { coverFileId: d.cover.fileId } : {}),
    ...clean({ authorName: orUndef(d.authorName), language: orUndef(d.language), pages: orUndef(d.pages) ? Number(d.pages.trim()) : undefined }),
    ...(has(original) ? { original: clean(original) } : {}),
    ...(has(translation) ? { translation: clean(translation) } : {}),
    ...clean({ recommendedAge: orUndef(d.recommendedAge) }),
    priceCents: priceCentsOf(d.price) ?? null,
    currency: d.currency || CURRENCY,
    ...clean({ shopUrl: d.shopUrl, supportUrl: orUndef(d.supportUrl) }),
  };
  return { title: d.title.trim(), description: orUndef(d.description), items: [{ fileId: d.pdf!.fileId }], book };
}
